import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeftRight,
  Check,
  ChevronLeft,
  Flag,
  Minus,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Square,
  Undo2,
  Video,
  X,
} from "lucide-react";
import { computeScoreboardSides } from "../utils/championshipScoreboardSides";
import { formatPointsValue, formatRankingPlace } from "../utils/flttRankingDisplay";
import ClubLogoMark from "./ClubLogoMark";
import PointOutcomeBar from "./PointOutcomeBar";
import "./ChampionshipScorePad.css";

function playerRankLine(player) {
  if (!player) return "";
  const bits = [];
  const level = String(player.classement || "").trim();
  if (level) bits.push(level);
  const points = formatPointsValue(player.ranking_points);
  if (points) bits.push(`${points} pts`);
  const place = formatRankingPlace(player.ranking_place);
  if (place) bits.push(place);
  return bits.join(" · ");
}

function playerLabel(players, fallback) {
  const names = (players || [])
    .map((p) => String(p?.name || "").trim())
    .filter((n) => n && n !== "—" && !/^Seat\s/i.test(n));
  return names.length ? names.join(" / ") : fallback || "—";
}

function selectInputValue(event) {
  const el = event?.target;
  if (!el || typeof el.select !== "function") return;
  requestAnimationFrame(() => {
    try {
      el.select();
    } catch {
      /* ignore */
    }
  });
}

/** Standard TT serve: every 2 points, every 1 from deuce; alternate first server by set. */
function estimateServingSide(liveHome, liveAway, completedSets) {
  const h = Math.max(0, Number(liveHome) || 0);
  const a = Math.max(0, Number(liveAway) || 0);
  const total = h + a;
  const first = completedSets % 2 === 0 ? "home" : "away";
  const deuce = h >= 10 && a >= 10;
  const serveIndex = deuce ? total : Math.floor(total / 2);
  const swap = serveIndex % 2 === 1;
  if (first === "home") return swap ? "away" : "home";
  return swap ? "home" : "away";
}

function softTint(hex, amount = 0.12) {
  const raw = String(hex || "").replace("#", "");
  if (!/^[0-9a-fA-F]{6}$/.test(raw)) return `rgba(8, 119, 188, ${amount})`;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${amount})`;
}

const LANDSCAPE_SCORE_QUERY = "(orientation: landscape) and (max-height: 640px)";

function useLandscapeScorePad() {
  const [on, setOn] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(LANDSCAPE_SCORE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(LANDSCAPE_SCORE_QUERY);
    const sync = () => setOn(mq.matches);
    sync();
    mq.addEventListener?.("change", sync);
    return () => mq.removeEventListener?.("change", sync);
  }, []);

  return on;
}

/** Scale score digits to fill the score cell (width + height). */
function useFitScoreSize(contentKey) {
  const wrapRef = useRef(null);
  const textRef = useRef(null);
  const [fontPx, setFontPx] = useState(96);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const text = textRef.current;
    if (!wrap || !text) return undefined;

    const fit = () => {
      const w = wrap.clientWidth;
      const h = wrap.clientHeight;
      if (w < 8 || h < 8) return;

      // Line box must stay inside the cell, or the digit ink is clipped.
      text.style.lineHeight = "1";
      const maxW = Math.max(8, w - 4);
      const maxH = Math.max(8, h - 6);
      let lo = 12;
      let hi = Math.max(lo, Math.floor(Math.min(maxW, maxH)));
      let best = lo;
      for (let i = 0; i < 16; i += 1) {
        const mid = Math.floor((lo + hi) / 2);
        text.style.fontSize = `${mid}px`;
        const fits = text.scrollWidth <= maxW && text.scrollHeight <= maxH;
        if (fits) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      text.style.fontSize = `${best}px`;
      setFontPx(best);
    };

    fit();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            window.requestAnimationFrame(fit);
          })
        : null;
    ro?.observe(wrap);
    window.addEventListener("resize", fit);
    window.addEventListener("orientationchange", fit);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", fit);
      window.removeEventListener("orientationchange", fit);
    };
  }, [contentKey]);

  return { wrapRef, textRef, fontPx };
}

/**
 * Match score pad — mockup layout with club brand colors.
 * Bottom tabs (sheet / camera / timeline) stay on the page chrome.
 */
export function ChampionshipScorePad({
  t,
  mode = "light",
  brandPrimary = "#0877bc",
  brandSecondary = "#c2410c",
  homeLogo = null,
  awayLogo = null,
  homeClubName = null,
  awayClubName = null,
  tableNumber,
  rubberCode,
  homeName,
  awayName,
  homePlayers,
  awayPlayers,
  liveHome,
  liveAway,
  setsHome,
  setsAway,
  setsToWin = 3,
  completedSets = [],
  setWinner,
  canScore,
  canUndo,
  canMinusHome,
  canMinusAway,
  canStartSet,
  scoreMode,
  editHome,
  editAway,
  busy,
  onBack,
  onAddPoint,
  onRemovePoint,
  onUndo,
  onStartSet,
  onScoreMode,
  onEditHome,
  onEditAway,
  onSaveEdit,
  manualFlip,
  onToggleManualFlip,
  canTagOutcome = false,
  activeOutcome = null,
  onOutcome,
  timeoutActive = false,
  canEndRubber = false,
  onEndRubber,
  canResetMatch = false,
  onResetMatch,
  canReleaseScore = false,
  onReleaseScore,
  showMatchRecording = false,
  matchRecording = false,
  recordBusy = false,
  onStartRecording,
  onEndRecording,
}) {
  const sides = computeScoreboardSides({
    homeSets: setsHome,
    awaySets: setsAway,
    liveHome,
    liveAway,
    setsToWin,
    manualFlip,
  });

  const sideRails = useLandscapeScorePad();
  const [cue, setCue] = useState(null);
  const [endOpen, setEndOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [endHome, setEndHome] = useState(String(setsToWin));
  const [endAway, setEndAway] = useState("0");
  const [endError, setEndError] = useState("");
  const prevKeyRef = useRef("");
  const editHomeRef = useRef(null);
  const editAwayRef = useRef(null);

  const completed = Array.isArray(completedSets) ? completedSets : [];
  const gameNumber = completed.length + (setWinner ? 0 : 1) || 1;
  const bestOf = setsToWin * 2 - 1;
  const rally = Math.max(0, liveHome + liveAway);
  const servingSide = estimateServingSide(liveHome, liveAway, completed.length);

  useEffect(() => {
    const key = [
      sides.completedSets,
      sides.fivePointFlip ? 1 : 0,
      setWinner || "",
    ].join(":");
    const prev = prevKeyRef.current;
    prevKeyRef.current = key;
    if (!prev) return;
    const [prevSets, prevFive] = prev.split(":");
    if (setWinner && Number(prevSets) < sides.completedSets) {
      setCue({
        kind: "set",
        text: t("cmChangeEndsSet", "Change ends — next set"),
      });
      return;
    }
    if (sides.fivePointFlip && prevFive === "0") {
      setCue({
        kind: "five",
        text: t("cmChangeEndsFive", "Change ends — 5 points"),
      });
    }
  }, [sides.completedSets, sides.fivePointFlip, setWinner, t]);

  useEffect(() => {
    if (!cue) return undefined;
    const id = window.setTimeout(() => setCue(null), 3800);
    return () => window.clearTimeout(id);
  }, [cue]);

  useEffect(() => {
    if (scoreMode !== "edit") return;
    editHomeRef.current?.focus();
    editHomeRef.current?.select?.();
  }, [scoreMode]);

  const leftIsHome = sides.leftSide === "home";
  const left = {
    side: sides.leftSide,
    name: leftIsHome ? homeName : awayName,
    players: leftIsHome ? homePlayers : awayPlayers,
    pts: leftIsHome ? liveHome : liveAway,
    sets: leftIsHome ? setsHome : setsAway,
    canMinus: leftIsHome ? canMinusHome : canMinusAway,
    tone: leftIsHome ? "home" : "away",
    serving: servingSide === sides.leftSide,
    editValue: leftIsHome ? editHome : editAway,
    onEdit: leftIsHome ? onEditHome : onEditAway,
    inputRef: leftIsHome ? editHomeRef : editAwayRef,
    accent: leftIsHome ? brandPrimary : brandSecondary,
    logo: leftIsHome ? homeLogo : awayLogo,
    clubName: leftIsHome ? homeClubName || homeName : awayClubName || awayName,
  };
  const right = {
    side: sides.rightSide,
    name: leftIsHome ? awayName : homeName,
    players: leftIsHome ? awayPlayers : homePlayers,
    pts: leftIsHome ? liveAway : liveHome,
    sets: leftIsHome ? setsAway : setsHome,
    canMinus: leftIsHome ? canMinusAway : canMinusHome,
    tone: leftIsHome ? "away" : "home",
    serving: servingSide === sides.rightSide,
    editValue: leftIsHome ? editAway : editHome,
    onEdit: leftIsHome ? onEditAway : onEditHome,
    inputRef: leftIsHome ? editAwayRef : editHomeRef,
    accent: leftIsHome ? brandSecondary : brandPrimary,
    logo: leftIsHome ? awayLogo : homeLogo,
    clubName: leftIsHome ? awayClubName || awayName : homeClubName || homeName,
  };

  const history = useMemo(() => {
    const cells = [];
    for (let i = 0; i < bestOf; i += 1) {
      const done = completed[i];
      if (done) {
        const lh = leftIsHome ? done.home : done.away;
        const rh = leftIsHome ? done.away : done.home;
        cells.push({
          n: i + 1,
          label: `${lh}–${rh}`,
          state: "done",
        });
      } else if (i === completed.length && !setWinner) {
        cells.push({
          n: i + 1,
          label: `${left.pts}–${right.pts}`,
          state: "live",
        });
      } else {
        cells.push({ n: i + 1, label: "– –", state: "empty" });
      }
    }
    return cells;
  }, [bestOf, completed, left.pts, leftIsHome, right.pts, setWinner]);

  const scoring = canScore && scoreMode === "input";
  const editing = canScore && scoreMode === "edit";

  const styleVars = {
    "--sp-home": brandPrimary || "#0877bc",
    "--sp-away": brandSecondary || "#c2410c",
    "--sp-home-soft": softTint(brandPrimary, mode === "dark" ? 0.22 : 0.14),
    "--sp-away-soft": softTint(brandSecondary, mode === "dark" ? 0.22 : 0.14),
  };

  const confirmEndRubber = async () => {
    const hs = Number(endHome);
    const as = Number(endAway);
    if (hs !== setsToWin && as !== setsToWin) {
      setEndError(
        t("cmFinalSetsNeedWin", "One side must reach {{n}} sets", { n: setsToWin })
      );
      return;
    }
    if (hs === setsToWin && as === setsToWin) {
      setEndError(t("cmFinalSetsBothWin", "Both sides cannot win"));
      return;
    }
    try {
      setEndError("");
      await onEndRubber?.({ home_sets: hs, away_sets: as, sets_to_win: setsToWin });
      setEndOpen(false);
    } catch (err) {
      setEndError(err?.message || String(err));
    }
  };

  return (
    <div
      className={`cm-scorepad cm-scorepad--${mode}${editing ? " is-editing" : ""}`}
      style={styleVars}
      role="region"
      aria-label={t("cmScorePad", "Live score pad")}
    >
      <header className="cm-scorepad__head">
        <button
          type="button"
          className="cm-scorepad__back"
          onClick={onBack}
          aria-label={t("cmAllMatches", "All matches")}
        >
          <ChevronLeft size={22} aria-hidden />
        </button>
        <div className="cm-scorepad__title">
          <strong>{t("cmMatch", "Match")}</strong>
          <span>
            {rubberCode}
            {tableNumber != null ? ` · ${t("cmTable", "Table")} ${tableNumber}` : ""}
          </span>
        </div>
        <div className="cm-scorepad__head-actions">
          <div className="cm-scorepad__badge">
            <em>
              {t("cmBestOf", "Best of {{n}}", { n: bestOf })}
            </em>
            <span>
              {t("cmGameOf", "Game {{n}} of {{total}}", {
                n: Math.min(gameNumber, bestOf),
                total: bestOf,
              })}
            </span>
          </div>
          <button
            type="button"
            className="cm-scorepad__more"
            onClick={() => setMoreOpen((v) => !v)}
            aria-label={t("cmMore", "More")}
            aria-expanded={moreOpen}
          >
            <MoreHorizontal size={20} aria-hidden />
          </button>
          {moreOpen ? (
            <div className="cm-scorepad__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  onToggleManualFlip?.();
                  setMoreOpen(false);
                }}
              >
                <ArrowLeftRight size={16} aria-hidden />
                {manualFlip
                  ? t("cmSidesManualOn", "Sides flipped")
                  : t("cmFlipSides", "Flip sides")}
              </button>
              {canScore ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onEditHome(String(liveHome));
                    onEditAway(String(liveAway));
                    onScoreMode("edit");
                    setMoreOpen(false);
                  }}
                >
                  <Pencil size={16} aria-hidden />
                  {t("cmEditScore", "Edit score")}
                </button>
              ) : null}
              {canReleaseScore ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMoreOpen(false);
                    void onReleaseScore?.();
                  }}
                >
                  <Undo2 size={16} aria-hidden />
                  {t("cmReleaseScore", "Release score")}
                </button>
              ) : null}
              {canEndRubber ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setEndHome(String(Math.max(setsHome, setsToWin)));
                    setEndAway(String(setsAway));
                    setEndError("");
                    setEndOpen(true);
                    setMoreOpen(false);
                  }}
                >
                  <Flag size={16} aria-hidden />
                  {t("cmEndRubber", "End match")}
                </button>
              ) : null}
              {canResetMatch ? (
                <button
                  type="button"
                  role="menuitem"
                  className="is-danger"
                  onClick={() => {
                    setMoreOpen(false);
                    void onResetMatch?.();
                  }}
                >
                  <RotateCcw size={16} aria-hidden />
                  {t("cmResetMatch", "Reset match")}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {showMatchRecording ? (
        <button
          type="button"
          className={`cm-scorepad__record${matchRecording ? " is-live" : ""}`}
          disabled={recordBusy}
          onClick={() =>
            void (matchRecording ? onEndRecording?.() : onStartRecording?.())
          }
          aria-pressed={matchRecording}
        >
          {matchRecording ? (
            <Square size={16} aria-hidden />
          ) : (
            <Video size={16} aria-hidden />
          )}
          {matchRecording
            ? t("cmEndRecording", "End recording")
            : t("cmStartRecording", "Start recording")}
        </button>
      ) : null}

      {cue ? (
        <p className="cm-scorepad__cue" role="status">
          {cue.text}
        </p>
      ) : null}

      <div className="cm-scorepad__board">
        <PlayerCard
          half={left}
          scoring={scoring}
          editing={editing}
          sideRails={sideRails}
          t={t}
          onPlus={() => onAddPoint(left.side)}
          onMinus={() => onRemovePoint(left.side)}
          onFocusNext={() => editAwayRef.current?.focus()}
        />
        <PlayerCard
          half={right}
          scoring={scoring}
          editing={editing}
          sideRails={sideRails}
          t={t}
          onPlus={() => onAddPoint(right.side)}
          onMinus={() => onRemovePoint(right.side)}
          onFocusNext={() => editHomeRef.current?.focus()}
        />
      </div>

      {setWinner ? (
        <p className="cm-scorepad__banner" role="status">
          {t("cmSetWinner", "{{name}} wins the set {{score}}", {
            name: setWinner === "home" ? homeName : awayName,
            score: `${liveHome}–${liveAway}`,
          })}
        </p>
      ) : null}

      {canStartSet ? (
        <button type="button" className="cm-scorepad__cta" onClick={onStartSet}>
          <ArrowLeftRight size={16} aria-hidden />
          {t("cmStartSetChangeEnds", "Change ends & start next set")}
        </button>
      ) : null}

      <div className="cm-scorepad__dock">
        <div className="cm-scorepad__midrow">
          <button
            type="button"
            className="cm-scorepad__undo"
            disabled={!canUndo}
            onClick={onUndo}
          >
            <Undo2 size={18} aria-hidden />
            <span>{t("cmUndoLastPoint", "Undo last point")}</span>
          </button>
          <div className="cm-scorepad__rally" aria-live="polite">
            {t("cmRallyN", "Rally {{n}}", { n: rally })}
          </div>
        </div>

        {canTagOutcome && onOutcome ? (
          <PointOutcomeBar
            t={t}
            activeOutcome={activeOutcome}
            disabled={busy || timeoutActive}
            onSelect={onOutcome}
          />
        ) : null}
      </div>

      {editing ? (
        <div className="cm-scorepad__edit-bar">
          <button
            type="button"
            className="cm-scorepad__cta"
            disabled={busy}
            onClick={() => void onSaveEdit()}
          >
            <Check size={16} aria-hidden />
            {t("cmSaveScore", "Set score")}
          </button>
          <button
            type="button"
            className="cm-scorepad__ghost"
            onClick={() => onScoreMode("input")}
          >
            {t("cancel", "Cancel")}
          </button>
        </div>
      ) : null}

      <div className="cm-scorepad__footer">
        <section className="cm-scorepad__history" aria-label={t("cmGameHistory", "Game history")}>
          <h3>{t("cmGameHistory", "Game history")}</h3>
          <ol>
            {history.map((cell) => (
              <li key={cell.n} className={`is-${cell.state}`}>
                <span>
                  {t("cmGameN", "Game {{n}}", { n: cell.n })}
                </span>
                <strong>{cell.label}</strong>
              </li>
            ))}
          </ol>
        </section>
        <button type="button" className="cm-scorepad__sheet-btn" onClick={onBack}>
          {t("cmAllMatches", "All matches")}
        </button>
      </div>

      {endOpen ? (
        <div className="cm-scorepad__sheet" role="presentation" onClick={() => setEndOpen(false)}>
          <div
            className="cm-scorepad__sheet-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="cm-scorepad__sheet-head">
              <h2>{t("cmEndRubberTitle", "End match — final sets")}</h2>
              <button type="button" onClick={() => setEndOpen(false)} aria-label={t("close", "Close")}>
                <X size={18} />
              </button>
            </header>
            <p>
              {t("cmEndRubberHint", "Enter the final sets result (first to {{n}}).", {
                n: setsToWin,
              })}
            </p>
            <div className="cm-scorepad__end-row">
              <label>
                <span>{homeName}</span>
                <input
                  inputMode="numeric"
                  value={endHome}
                  onFocus={selectInputValue}
                  onChange={(e) => setEndHome(e.target.value.replace(/[^\d]/g, ""))}
                />
              </label>
              <em>–</em>
              <label>
                <span>{awayName}</span>
                <input
                  inputMode="numeric"
                  value={endAway}
                  onFocus={selectInputValue}
                  onChange={(e) => setEndAway(e.target.value.replace(/[^\d]/g, ""))}
                />
              </label>
            </div>
            {endError ? <p className="cm-scorepad__end-error">{endError}</p> : null}
            <button
              type="button"
              className="cm-scorepad__cta"
              disabled={busy}
              onClick={() => void confirmEndRubber()}
            >
              {t("cmConfirmEndRubber", "Save final result")}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PlayerCard({ half, scoring, editing, sideRails, t, onPlus, onMinus, onFocusNext }) {
  const { wrapRef, textRef, fontPx } = useFitScoreSize(
    `${editing ? "e" : "s"}:${half.pts}:${half.editValue}`
  );

  const setScoreEl = (el) => {
    textRef.current = el;
    if (editing && half.inputRef) half.inputRef.current = el;
  };

  const rankLines = (half.players || [])
    .map((player) => playerRankLine(player))
    .filter(Boolean);

  return (
    <section
      className={`cm-scorepad__card cm-scorepad__card--${half.tone}${
        half.serving ? " is-serving" : ""
      }`}
      style={{ "--sp-card-accent": half.accent }}
    >
      <div className="cm-scorepad__card-top">
        <div className="cm-scorepad__who-row">
          {half.serving ? (
            <span className="cm-scorepad__serve" aria-label={t("cmServing", "Serving")}>
              <i aria-hidden />
              {t("cmServing", "Serving")}
            </span>
          ) : null}
          <span className="cm-scorepad__who-block">
            <strong className="cm-scorepad__who">
              {playerLabel(half.players, half.name)}
            </strong>
            {rankLines.length ? (
              <span className="cm-scorepad__rank">
                {rankLines.map((line, idx) => (
                  <span key={`${line}-${idx}`} className="cm-scorepad__rank-line">
                    {line}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
        </div>
        <div className="cm-scorepad__meta">
          <span
            className="cm-scorepad__games"
            aria-label={t("cmGamesWonShort", "Games {{n}}", { n: half.sets })}
          >
            <strong>{half.sets}</strong>
            <em>{t("cmSets", "Sets")}</em>
          </span>
          <ClubLogoMark
            clubImage={half.logo}
            clubName={half.clubName}
            size="sm"
            className="cm-scorepad__club-logo"
            title={half.clubName || ""}
          />
        </div>
      </div>

      <div className="cm-scorepad__score-wrap" ref={wrapRef}>
        {editing ? (
          <input
            ref={setScoreEl}
            className="cm-scorepad__pts-input"
            style={{ fontSize: `${fontPx}px` }}
            inputMode="numeric"
            value={half.editValue}
            aria-label={half.name}
            onFocus={selectInputValue}
            onChange={(e) => half.onEdit(e.target.value.replace(/[^\d]/g, ""))}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === "Tab") {
                e.preventDefault();
                onFocusNext?.();
              }
            }}
          />
        ) : sideRails ? (
          <span
            ref={setScoreEl}
            className="cm-scorepad__pts cm-scorepad__pts--read"
            style={{ fontSize: `${fontPx}px` }}
          >
            {half.pts}
          </span>
        ) : (
          <button
            type="button"
            className="cm-scorepad__pts"
            disabled={!scoring}
            onClick={onPlus}
            aria-label={`${t("cmPlusPoint", "Plus one point")} ${half.name}`}
          >
            <span
              ref={setScoreEl}
              className="cm-scorepad__pts-num"
              style={{ fontSize: `${fontPx}px` }}
            >
              {half.pts}
            </span>
          </button>
        )}
      </div>

      <div
        className={`cm-scorepad__ops${scoring ? "" : " is-empty"}`}
        role={scoring ? "group" : undefined}
        aria-label={scoring ? half.name : undefined}
      >
        {scoring ? (
          <>
            <button
              type="button"
              className="cm-scorepad__minus"
              disabled={!half.canMinus}
              onClick={onMinus}
              aria-label={`${t("cmMinusPoint", "Minus one point")} ${half.name}`}
            >
              <Minus size={26} strokeWidth={3} aria-hidden />
            </button>
            <button
              type="button"
              className="cm-scorepad__plus"
              onClick={onPlus}
              aria-label={`${t("cmPlusPoint", "Plus one point")} ${half.name}`}
            >
              <Plus size={32} strokeWidth={3} aria-hidden />
            </button>
          </>
        ) : null}
      </div>
    </section>
  );
}

export default ChampionshipScorePad;
